import React, { useMemo, useState } from 'react';
import { useApiData } from '../../api/client';
import type {
  AuditLogEntry,
  PaginatedResponse,
  GraphNode,
  GraphEdge,
  RollbackPreview,
  RollbackResult,
} from '../../api/dastarkhan';
import { useDastarkhanApi } from '../../api/dastarkhan';
import { useAccessToken } from '../../auth/hooks';
import { exportProjectBackup } from '../../utils/exportGraph';
import {
  Table,
  Button,
  Select,
  Modal,
  Icon,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface AuditLogProps {
  projectId: string;
  isAdmin?: boolean;
  graphNodes?: GraphNode[];
  graphEdges?: GraphEdge[];
  projectName?: string;
  onGraphRefetch?: () => void;
}

export const AuditLog: React.FC<AuditLogProps> = ({
  projectId,
  isAdmin = false,
  graphNodes = [],
  graphEdges = [],
  projectName = 'project',
  onGraphRefetch,
}) => {
  const { theme } = useTheme();
  const { previewRollback, executePointInTime, continueRollback } = useDastarkhanApi();
  const token = useAccessToken();
  const [backupError, setBackupError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [operationFilter, setOperationFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  // Admin rollback state
  const [targetTransactionId, setTargetTransactionId] = useState<number | null>(null);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<RollbackPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<RollbackResult | null>(null);
  const [showConflict, setShowConflict] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  const endpoint = useMemo(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(page));
    searchParams.set('per_page', '50');
    if (operationFilter) searchParams.set('operation', operationFilter);
    if (entityFilter) searchParams.set('entity_type', entityFilter);
    return `/dastarkhan/projects/${projectId}/audit-log?${searchParams.toString()}`;
  }, [projectId, page, operationFilter, entityFilter]);

  const { data, loading, error: fetchError, refetch: refetchAuditLog } = useApiData<PaginatedResponse<AuditLogEntry>>(endpoint);
  const entries = data?.items || [];
  const total = data?.total || 0;
  const error = fetchError ? 'Failed to load audit log' : null;

  const operationOptions = [
    { value: '', label: 'All Operations' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'rollback', label: 'Rollback' },
  ];

  const entityOptions = [
    { value: '', label: 'All Types' },
    { value: 'node', label: 'Node' },
    { value: 'edge', label: 'Edge' },
  ];

  const operationColors: Record<string, string> = {
    create: theme.colors.semantic.success.default,
    update: theme.colors.semantic.warning.default,
    delete: theme.colors.semantic.error.default,
    rollback: theme.colors.semantic.info.default,
  };

  // Admin rollback handlers
  const handleRollBackToHere = (transactionId: number) => {
    setTargetTransactionId(transactionId);
    setShowBackupPrompt(true);
  };

  const handleDownloadBackup = async () => {
    setBackupError(null);
    if (!token) {
      setBackupError('Not authenticated. Please sign in again.');
      return;
    }
    try {
      await exportProjectBackup(token, projectId, projectName);
    } catch (err) {
      setBackupError(err instanceof Error ? err.message : 'Backup export failed');
      return;
    }
    setShowBackupPrompt(false);
    loadPreview();
  };

  const handleSkipBackup = () => {
    setShowBackupPrompt(false);
    loadPreview();
  };

  const loadPreview = async () => {
    if (!targetTransactionId) return;
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const result = await previewRollback(projectId, targetTransactionId);
      setPreviewData(result ?? null);
    } catch { /* handled */ }
    finally { setPreviewLoading(false); }
  };

  const handleExecuteRollback = async () => {
    if (!targetTransactionId) return;
    setShowPreview(false);
    setShowProgress(true);
    try {
      const result = await executePointInTime(projectId, targetTransactionId);
      if (!result) return;
      setRollbackResult(result);
      if (result.status === 'conflict' && result.conflict) {
        setShowProgress(false);
        setShowConflict(true);
      } else {
        setShowProgress(false);
        setShowComplete(true);
        onGraphRefetch?.();
        refetchAuditLog();
      }
    } catch {
      setShowProgress(false);
    }
  };

  const handleConflictAction = async (action: 'skip' | 'force' | 'abort') => {
    if (!rollbackResult?.rollback_batch_id || !rollbackResult?.conflict) return;
    setShowConflict(false);
    if (action === 'abort') {
      setShowComplete(true);
      onGraphRefetch?.();
      refetchAuditLog();
      return;
    }
    setShowProgress(true);
    try {
      const result = await continueRollback(
        projectId,
        rollbackResult.rollback_batch_id,
        action,
        rollbackResult.conflict.transaction_id,
      );
      if (!result) return;
      setRollbackResult(result);
      if (result.status === 'conflict' && result.conflict) {
        setShowProgress(false);
        setShowConflict(true);
      } else {
        setShowProgress(false);
        setShowComplete(true);
        onGraphRefetch?.();
        refetchAuditLog();
      }
    } catch {
      setShowProgress(false);
    }
  };

  const closeAll = () => {
    setShowBackupPrompt(false);
    setShowPreview(false);
    setShowProgress(false);
    setShowConflict(false);
    setShowComplete(false);
    setTargetTransactionId(null);
    setPreviewData(null);
    setRollbackResult(null);
    setBackupError(null);
  };

  const columns = [
    {
      key: 'created_at',
      header: 'Time',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const e = row as unknown as AuditLogEntry;
        return <span style={{ fontSize: '0.8rem' }}>{new Date(e.created_at).toLocaleString()}</span>;
      },
    },
    { key: 'user_email', header: 'User' },
    {
      key: 'operation',
      header: 'Operation',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const e = row as unknown as AuditLogEntry;
        const color = operationColors[e.operation] || theme.colors.text.primary;
        return (
          <Flex gap="xs" align="center">
            <span style={{
              padding: `2px ${spacing.xs}`,
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              opacity: e.is_rolled_back ? 0.6 : 1,
              textDecoration: e.is_rolled_back ? 'line-through' : 'none',
            }}>
              {e.operation}
            </span>
            {e.is_rolled_back && (
              <span style={{ fontSize: '0.65rem', color: theme.colors.text.secondary, fontStyle: 'italic' }}>
                rolled back
              </span>
            )}
            {e.operation === 'rollback' && e.original_transaction_id != null && (
              <span style={{ fontSize: '0.65rem', color: theme.colors.text.secondary }}>
                Undid #{e.original_transaction_id}
              </span>
            )}
            {e.rollback_batch_id && (
              <span style={{ fontSize: '0.6rem', color: theme.colors.text.secondary, fontFamily: 'monospace' }}>
                Batch:{e.rollback_batch_id.slice(0, 8)}
              </span>
            )}
          </Flex>
        );
      },
    },
    { key: 'entity_type', header: 'Type' },
    {
      key: 'entity_neo4j_id',
      header: 'Entity ID',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const e = row as unknown as AuditLogEntry;
        return <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{e.entity_neo4j_id}</span>;
      },
    },
    ...(isAdmin ? [{
      key: '_actions',
      header: '',
      render: (_v: unknown, row: Record<string, unknown>) => {
        const e = row as unknown as AuditLogEntry;
        if (e.is_rolled_back) return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRollBackToHere(e.id)}
            title="Roll back the project to this point in time"
          >
            <Flex gap="xs" align="center">
              <Icon name="arrow-left" size={12} />
              <span style={{ fontSize: '0.7rem' }}>Roll Back To Here</span>
            </Flex>
          </Button>
        );
      },
    }] : []),
  ];

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.text.primary }}>
          Transaction Log ({total} entries)
        </h3>
        <Flex gap="sm">
          <Select
            size="sm"
            value={operationFilter}
            onChange={(value: string) => { setOperationFilter(value); setPage(1); }}
            options={operationOptions}
          />
          <Select
            size="sm"
            value={entityFilter}
            onChange={(value: string) => { setEntityFilter(value); setPage(1); }}
            options={entityOptions}
          />
        </Flex>
      </Flex>

      {error && <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>{error}</div>}

      <Table
        data={entries as unknown as Record<string, unknown>[]}
        columns={columns}
        loading={loading}
        emptyMessage="No audit log entries"
      />

      {total > 50 && (
        <Flex justify="center" gap="sm">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </Button>
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, alignSelf: 'center' }}>
            Page {page} of {Math.ceil(total / 50)}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * 50 >= total}>
            Next
          </Button>
        </Flex>
      )}

      {/* Backup prompt modal */}
      {showBackupPrompt && (
        <Modal isOpen onClose={closeAll}>
          <Modal.Header>Download Backup Before Rollback</Modal.Header>
          <Modal.Body>
            <Stack gap="sm">
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                It is recommended to download an Excel export of the current graph before proceeding. This action cannot be undone.
              </p>
              {backupError && (
                <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.semantic.error.default }}>
                  {backupError}
                </p>
              )}
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" size="sm" onClick={closeAll}>Cancel</Button>
              <Button variant="ghost" size="sm" onClick={handleSkipBackup}>Skip Backup</Button>
              <Button variant="solid" size="sm" onClick={handleDownloadBackup}>Download Backup</Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}

      {/* Preview modal */}
      {showPreview && (
        <Modal isOpen onClose={closeAll}>
          <Modal.Header>Confirm Point-in-Time Rollback</Modal.Header>
          <Modal.Body>
            {previewLoading ? (
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>Loading rollback preview...</p>
            ) : previewData ? (
              <Stack gap="sm">
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  <strong>Transactions to undo:</strong> {previewData.total_transactions}
                </p>
                <div style={{ fontSize: '0.85rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.xs }}>
                  {Object.entries(previewData.operations_summary).map(([op, count]) => (
                    <span key={op}>{op}: {count}</span>
                  ))}
                </div>
                {previewData.affected_entities.length > 0 && (
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600 }}>Affected entities: {previewData.affected_entities.length}</p>
                  </div>
                )}
                {previewData.affected_users.length > 0 && (
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600 }}>Users affected: {previewData.affected_users.length}</p>
                    <div style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                      {previewData.affected_users.map((u) => u.user_email || u.user_id).join(', ')}
                    </div>
                  </div>
                )}
              </Stack>
            ) : (
              <p style={{ margin: 0, color: theme.colors.semantic.error.default }}>Failed to load preview</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" size="sm" onClick={closeAll}>Cancel</Button>
              <Button variant="solid" size="sm" onClick={handleExecuteRollback} disabled={previewLoading || !previewData}>
                Roll Back
              </Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}

      {/* Progress modal */}
      {showProgress && (
        <Modal isOpen onClose={() => {}}>
          <Modal.Header>Rolling Back...</Modal.Header>
          <Modal.Body>
            <p style={{ margin: 0, color: theme.colors.text.secondary }}>
              Rolling back transactions. Please wait...
            </p>
          </Modal.Body>
        </Modal>
      )}

      {/* Conflict modal */}
      {showConflict && rollbackResult?.conflict && (
        <Modal isOpen onClose={closeAll}>
          <Modal.Header>Conflict During Rollback</Modal.Header>
          <Modal.Body>
            <Stack gap="sm">
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                Rolled back {rollbackResult.processed_so_far ?? rollbackResult.total_processed} transaction(s).
                Conflict at transaction #{rollbackResult.conflict.transaction_id}:
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>
                Entity state mismatch detected. The entity may have been modified outside tracked operations.
              </p>
              {rollbackResult.remaining_count != null && (
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  {rollbackResult.remaining_count} transaction(s) remaining.
                </p>
              )}
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <Flex gap="sm" justify="end">
              <Button variant="ghost" size="sm" onClick={() => handleConflictAction('abort')}>Stop Rollback</Button>
              <Button variant="outline" size="sm" onClick={() => handleConflictAction('skip')}>Skip This Transaction</Button>
              <Button variant="solid" size="sm" onClick={() => handleConflictAction('force')}>Force This Transaction</Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}

      {/* Completion modal */}
      {showComplete && rollbackResult && (
        <Modal isOpen onClose={closeAll}>
          <Modal.Header>Rollback Complete</Modal.Header>
          <Modal.Body>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Successfully rolled back {rollbackResult.total_processed} transaction(s)
              {(rollbackResult.total_skipped ?? 0) > 0 && ` (${rollbackResult.total_skipped} skipped)`}.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Flex justify="end">
              <Button variant="solid" size="sm" onClick={closeAll}>Close</Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </Stack>
  );
};
